import {Box, Button, Tooltip} from "@mui/material";
import {useNavigate} from "@tanstack/react-router";
import {CopyPlus, QrCode, Users} from "lucide-react";
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_Row,
    useMaterialReactTable,
} from "material-react-table";
import {type ReactElement, useState} from "react";
import FeeQrDialog from "@/components/Fees/FeeQrDialog.tsx";
import {buildFeeLink} from "@/Helpers/buildFeeLink.ts";
import {type MyFee, useMyFees} from "@/hooks/Instructor/useMyFees.ts";

const columns: MRT_ColumnDef<MyFee>[] = [
    {
        accessorKey: "name",
        header: "Name",
    },
    {
        accessorKey: "school.shortName",
        header: "School",
    },
    {
        accessorKey: "schoolYear",
        header: "Year",
    },
    {
        accessorKey: "type",
        header: "type",
    },
];

type Props = {
    onMakeFeeRequest: (fee: MyFee) => void;
};

const InstructorFeesTable = ({onMakeFeeRequest}: Props): ReactElement => {
    const {myFees, error} = useMyFees();
    const navigate = useNavigate();
    const [linkedFee, setLinkedFee] = useState<MyFee | null>(null);

    const rowActions = ({row}: {row: MRT_Row<MyFee>}) => (
        <Box sx={{display: "flex", flexWrap: "nowrap", gap: "8px"}}>
            <Tooltip title="Fee Details">
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Users size={16} />}
                    onClick={() => {
                        navigate({to: `/instructor/my-fee/${row.original.id}`}).catch(e =>
                            console.error("navigate error", e),
                        );
                    }}
                >
                    View Students
                </Button>
            </Tooltip>
            {buildFeeLink(row.original, window.origin) && (
                <Tooltip title="Show the link and QR code for this fee">
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<QrCode size={16} />}
                        onClick={() => setLinkedFee(row.original)}
                    >
                        QR Code
                    </Button>
                </Tooltip>
            )}
            <Tooltip title="Start a new fee request from this fee">
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<CopyPlus size={16} />}
                    onClick={() => onMakeFeeRequest(row.original)}
                >
                    Duplicate
                </Button>
            </Tooltip>
        </Box>
    );

    const table = useMaterialReactTable<MyFee>({
        columns: columns,
        data: myFees,
        initialState: {
            sorting: [
                {id: "schoolYear", desc: true},
                {id: "name", desc: false},
            ],
        },
        enableRowActions: true,
        positionActionsColumn: "last",
        displayColumnDefOptions: {
            "mrt-row-actions": {
                header: "Actions",
            },
        },
        muiTablePaperProps: {
            style: {
                width: "100%",
            },
        },
        renderRowActions: rowActions,
    });

    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    backgroundColor: "#fff",
                    px: 2,
                    pt: 2,
                    borderRadius: 1,
                }}
            >
                {error}
            </Box>
        );
    }
    return (
        <>
            <MaterialReactTable table={table} />
            <FeeQrDialog fee={linkedFee} onClose={() => setLinkedFee(null)} />
        </>
    );
};

export default InstructorFeesTable;
